import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { isTicketReadOnly, currentStaffRole } from '../constants/staffRoleAccess';
import AdminLayout from '../components/AdminLayout';
import StaffComposer from '../components/admin/StaffComposer';
import RichMessageBody, { hasRichBody } from '../components/RichMessageBody';
import { TICKET_TEAMS, ownerToneColor, ownerText } from '../constants/ticketRoles';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const label = (s) => (s || '').replace(/_/g, ' ');
// ⚠️ EVERY .map() ON THIS PAGE GOES THROUGH THIS. Four separate lists come off
// four endpoints, and any one of them arriving as something other than an array
// — a shape change, an error body, a proxy returning HTML — is `.map is not a
// function` during render, which the root boundary turns into a full-page
// "Something went wrong" with nothing said about which list it was. A list this
// page cannot read renders as empty; the page still works.
const asArray = (v) => (Array.isArray(v) ? v : []);
const card = { background: '#fff', border: '1px solid #f3f4f6', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' };
const sel = { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#fff', width: '100%' };
const btn = (bg, fg) => ({ padding: '9px 14px', background: bg, color: fg, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' });

function Field({ k, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>{k}</div>
      <div style={{ fontSize: 14, color: '#111827' }}>{children}</div>
    </div>
  );
}

export default function AdminTicketDetail() {
  const { ref } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('doorly_admin_token');
  const headers = { Authorization: `Bearer ${token}` };
  const staffId = (() => { try { return JSON.parse(localStorage.getItem('doorly_admin_user') || '{}').id; } catch { return null; } })();

  const [t, setT] = useState(null);
  const [roster, setRoster] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  // ⚠️ SAVING WAS INVISIBLE. The button flipped to "Saving…" for one request and
  // then back, with no confirmation — the only way to know it worked was to
  // return to the queue and notice the ticket had dropped off. A write with no
  // acknowledgement reads as a broken button, and the natural response is to
  // click it again.
  const [saved, setSaved] = useState('');
  // Editable fields
  const [ed, setEd] = useState({ assigned_admin_id: '', status: '', priority: '', resolution: '' });

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/tickets/${encodeURIComponent(ref)}`, { headers });
      setT(r.data);
      setEd({
        assigned_admin_id: r.data.assigned_admin_id || '',
        status: r.data.status || 'open',
        priority: r.data.priority || 'medium',
        resolution: r.data.resolution || '',
      });
    } catch (e) { setErr(e.response?.status === 404 ? 'Ticket not found.' : 'Could not load ticket.'); }
    setLoading(false);
  }, [ref, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  // ⚠️ OWNERSHIP HISTORY, LOADED BY TICKET ID once the ticket resolves. Without
  // this the page shows only where a ticket ENDED UP, and "Engineering owns
  // this" silently erases that Client Success owned it first and handed it over
  // — which is the fact anyone reviewing the handling actually needs.
  useEffect(() => {
    if (!t?.id) return;
    axios.get(`${API}/api/admin/tickets/${t.id}/transfers`, { headers })
      .then((r) => setTransfers(asArray(r.data?.transfers))).catch(() => setTransfers([]));
  }, [t?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { axios.get(`${API}/api/admin/roster`, { headers }).then((r) => setRoster(asArray(r.data))).catch(() => {}); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── the reply path ──────────────────────────────────────────────────────────
  // ⚠️ TWO KINDS OF WRITING, AND THE UI MUST NEVER LET THEM LOOK ALIKE. A reply
  // leaves the building over a named person's signature; an internal note is
  // read only by Doorly. They share a textarea and nothing else — different
  // colour, different border, different header, different button, different
  // wording, and a different-looking row in the history afterwards. An internal
  // note that looks like a reply is how someone eventually sends one to a
  // borrower, and a colour alone is not enough to carry that.
  // ── (2) owner transfer ──────────────────────────────────────────────────
  const [moveTo, setMoveTo] = useState('');
  const [moveReason, setMoveReason] = useState('');
  const [moving, setMoving] = useState(false);

  const doTransfer = async () => {
    if (!moveTo) return;
    setMoving(true); setErr('');
    try {
      await axios.post(`${API}/api/admin/tickets/${t.id}/transfer`,
        { to_role: moveTo, reason: moveReason || null }, { headers });
      setMoveTo(''); setMoveReason('');
      await load();
      // The transfer list is what makes this auditable, so refresh it too —
      // a move whose record does not appear reads as a move that did not happen.
      const r = await axios.get(`${API}/api/admin/tickets/${t.id}/transfers`, { headers }).catch(() => null);
      if (r) setTransfers(r.data?.transfers || []);
      setSaved(`Moved to ${label(moveTo)} — recorded in the ownership history.`);
      setTimeout(() => setSaved(''), 5000);
    } catch (e) { setErr(e.response?.data?.error || 'Could not move this ticket.'); }
    setMoving(false);
  };

  const [msgs, setMsgs] = useState([]);
  const [sig, setSig] = useState(null);
  const [mode, setMode] = useState('reply');
  const [withNmls, setWithNmls] = useState(false);
  const [sending, setSending] = useState(false);
  const [msgErr, setMsgErr] = useState('');
  const [msgOk, setMsgOk] = useState('');
  const isNote = mode === 'internal_note';

  const loadMessages = useCallback(async () => {
    if (!t?.id) return;
    try {
      const r = await axios.get(`${API}/api/admin/tickets/${t.id}/messages`, { headers });
      setMsgs(asArray(r.data?.messages));
      setSig(r.data?.signature || null);
    } catch { setMsgs([]); }
  }, [t?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadMessages(); }, [loadMessages]);

  const save = async (over = {}) => {
    const payload = { ...ed, ...over };
    if (payload.status === 'resolved' && !String(payload.resolution || '').trim()) {
      setErr('A resolution is required to close this ticket.'); return;
    }
    setErr(''); setSaving(true);
    try {
      await axios.put(`${API}/api/admin/tickets/${t.id}`, {
        assigned_admin_id: payload.assigned_admin_id === '' ? null : Number(payload.assigned_admin_id),
        status: payload.status,
        priority: payload.priority,
        resolution: payload.resolution,
      }, { headers });
      await load();
      // Name what changed, not just "saved" — the status change is the one that
      // removes the ticket from the default queue, which is the surprising part.
      setSaved(payload.status === 'resolved' ? 'Saved — resolved and removed from the open queue.' : 'Saved.');
      setTimeout(() => setSaved(''), 4000);
    } catch (e) { setErr(e.response?.data?.error || 'Save failed.'); }
    setSaving(false);
  };
  const assignToMe = () => { setEd((p) => ({ ...p, assigned_admin_id: staffId })); save({ assigned_admin_id: staffId }); };

  // The address a reply would actually go to, shown before anyone types — not
  // discovered after sending. Mirrors the server's own order of preference.
  const replyTo = t ? (t.email || t.external_email || null) : null;

  // ── 🚨 WHO MAY WRITE TO THIS TICKET, AS OPPOSED TO READ IT ────────────────
  //
  // partner_success can now OPEN a borrower ticket whose borrower is a referral
  // from a partner they own (backend/utils/ticketVisibility.js), and still cannot
  // reply to it or move it. Without this flag the rep is shown a Reply box, writes
  // an answer to a borrower, presses send and collects a 403 — their words lost on
  // a control that should never have been offered.
  //
  // ⚠️ DERIVED FROM THE TICKET, NOT JUST THE ROLE. partner_success writes to
  // PARTNER tickets all day; that is the desk. Only borrower-subject tickets are
  // read-only for them.
  const readOnly = isTicketReadOnly(currentStaffRole(), t?.user_type);

  // 🔑 THE SHARED COMPOSER (approved v3, Jesse/Hannah 2026-11-02): payload = { body_html, body, attachment_data?,
  // attachment_name? } from StaffComposer. body_html is sanitized on the server against the one allowlist, and a
  // reply's EMAIL carries the formatting (HTML + a plain-text part) and the file. A file is checked and virus-scanned
  // on the server; if the scanner is unavailable the reply is HELD (202, "checking your file"): nothing is saved or
  // emailed until it is released. Resolves true when saved, so the composer clears; a refusal throws so the composer
  // shows it and keeps the draft.
  const sendMessage = async (payload) => {
    const text = String((payload && payload.body) || '').trim();
    if (!text && !(payload && payload.attachment_data)) throw new Error('Write something first.');
    if (!isNote && !replyTo) throw new Error('No email address on file for this requester.');
    setMsgErr(''); setMsgOk(''); setSending(true);
    try {
      // ⚠️ NO status OR resolution IN THIS PAYLOAD, and the endpoint refuses
      // them outright if one ever appears. Sending an answer is not closing a
      // ticket — closing stays in Manage, where it still needs a resolution.
      const r = await axios.post(`${API}/api/admin/tickets/${t.id}/reply`, {
        body: payload.body, body_html: payload.body_html, kind: mode, include_nmls: !isNote && withNmls,
        ...(payload.attachment_data ? { attachment_data: payload.attachment_data, attachment_name: payload.attachment_name } : {}),
      }, { headers });
      setWithNmls(false);
      // 202: the file is being checked (scanner unavailable, VIRUS_SCAN_ENFORCE on) — held, not saved, not sent yet.
      if (r.status === 202) {
        setMsgOk((r.data && (r.data.message || r.data.error)) || 'We’re checking your file. The reply goes out as soon as the check is done.');
        setTimeout(() => setMsgOk(''), 9000);
        setSending(false);
        return true;
      }
      await loadMessages();
      if (!isNote) await load();
      setMsgOk(r.data?.kind === 'internal_note'
        ? 'Internal note saved. Nobody outside Doorly can see it.'
        : `Reply sent to ${r.data?.recipient_email || 'the requester'}. The ticket is still open — resolve it below when it's done.`);
      setTimeout(() => setMsgOk(''), 7000);
      setSending(false);
      return true;
    } catch (e) {
      // A send that failed still left a row; show it rather than pretend
      // nothing happened.
      loadMessages();
      setSending(false);
      throw new Error(e.response?.data?.error || 'Could not send.');
    }
  };

  if (loading) return <AdminLayout><div style={{ padding: 32 }}>Loading…</div></AdminLayout>;
  if (!t) return <AdminLayout><div style={{ padding: 32 }}>{err || 'Not found.'} <button onClick={() => navigate('/admin/dashboard/customer-service')} style={{ ...btn('#3a6ad9', '#fff'), marginLeft: 12 }}>Back to queue</button></div></AdminLayout>;

  // ⚠️ AN EXTERNAL TICKET HAS NO ACCOUNT TO LINK TO. user_id is NULL by design,
  // so linking produced /admin/borrowers/null — a dead route, and the label read
  // "#null".
  const rType = t.requester_user_type || t.user_type || 'borrower';
  const isExternal = rType === 'external' || t.user_id == null;
  const requesterLink = isExternal ? null
    : (rType === 'partner' ? `/admin/partners/${t.user_id}` : `/admin/borrowers/${t.user_id}`);
  // The ask is not always a borrower's. Hardcoding one requester type into the
  // label is how a partner ticket came to say "What the borrower needs".
  const needsLabel = { borrower: 'What the borrower needs', partner: 'What the partner needs',
    external: 'What they need' }[rType] || 'What they need';

  return (
    <AdminLayout>
      <div style={{ padding: '28px 32px', maxWidth: 1100, margin: '0 auto' }}>
        <button onClick={() => navigate('/admin/dashboard/customer-service')} style={{ background: 'none', border: 'none', color: '#3a6ad9', cursor: 'pointer', fontSize: 13, fontWeight: 600, marginBottom: 14 }}>← Ticket queue</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#111827', margin: 0 }}>{t.ticket_number}</h1>
          {t.aging && <span style={{ fontSize: 11, fontWeight: 800, color: '#ff082f', background: '#ffe4e6', padding: '3px 8px', borderRadius: 9999 }}>AGING · {t.age_hours}h</span>}
          {/* ⚠️ REOPENED IS NOT THE SAME AS NEVER RESOLVED. Someone closed this
              and the requester came back — which is a different thing to read
              than a ticket that was simply never answered — which is all the
              queue would otherwise show. */}
          {t.reopened_at && (
            <span title={`The requester replied after this was resolved, so it reopened automatically`}
              style={{ fontSize: 11, fontWeight: 800, color: '#b45309', background: '#fef3c7', padding: '3px 8px', borderRadius: 9999 }}>
              REOPENED BY REQUESTER
            </span>
          )}
        </div>
        <p style={{ fontSize: 14, color: '#374151', margin: '0 0 20px' }}>{t.subject}</p>

        {/* 🚨 minWidth: 0 ON BOTH COLUMNS, AND IT IS THE ACTUAL FIX FOR THE
            OVERFLOW. A grid item defaults to min-width:auto, which refuses to
            shrink below its content's intrinsic minimum — so ONE long unbroken
            token anywhere inside pushes the whole column past the right margin
            and the page scrolls sideways. The ticket that exposed it carried a
            3,744-character base64 image in its summary; the data is repaired, but
            the layout must not depend on nobody ever pasting a long URL again. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 20 }} className="admin-two-col-grid">
          {/* Left: ticket facts + transcript */}
          <div style={{ minWidth: 0 }}>
            <div style={{ ...card, marginBottom: 20 }}>
              <Field k="Topic"><span style={{ textTransform: 'capitalize' }}>{label(t.topic)}</span></Field>
              <Field k={needsLabel}>
                {/* 🔑 A DoorlyPro message's formatting (summary_html, sanitized on the server — utils/messageHtml). */}
                {t.summary_html
                  ? <RichMessageBody msg={{ body_html: t.summary_html }} />
                  : <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{t.summary || t.description || '—'}</span>}
              </Field>
              <Field k="Requester">
                {requesterLink
                  ? <a href={requesterLink} style={{ color: '#3a6ad9', textDecoration: 'none', fontWeight: 600 }}>{t.requester_name || `#${t.user_id}`}</a>
                  : <span style={{ fontWeight: 600, color: '#111827' }}>{t.requester_name || 'Name not given'}</span>}
                <span style={{ color: '#9ca3af' }}> ({rType}{t.loan_number ? ` · ${t.loan_number}` : ''})</span>
                {/* ⚠️ SURFACED, NEVER APPLIED. A public form proves nothing about
                    who submitted it, so the name is shown and marked rather than
                    treated as an identity. */}
                {t.requester_verified === false && (
                  <span title="Submitted through a public form — this name and address are unverified."
                        style={{ marginLeft: 8, fontSize: 10, fontWeight: 800, color: '#b45309', background: '#fef3c7', padding: '2px 7px', borderRadius: 9999 }}>
                    UNVERIFIED
                  </span>
                )}
                {/* 🔑 FILED ON BEHALF (push C): the server's own sentence. */}
                {t.submitted_by_line && (
                  <div data-testid="ticket-submitted-by" style={{ fontSize: 12.5, color: '#6b7280', marginTop: 4 }}>
                    {t.submitted_by_line}
                  </div>
                )}
              </Field>
              {/* Contact details as FIELDS. They used to be packed into the
                  summary prose, which rendered as one unreadable blob. */}
              {isExternal && (t.external_email || t.external_phone) && (
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                  <Field k="Email">
                    {t.external_email
                      ? <a href={`mailto:${t.external_email}`} style={{ color: '#3a6ad9', textDecoration: 'none' }}>{t.external_email}</a>
                      : '—'}
                  </Field>
                  <Field k="Phone">
                    {t.external_phone
                      ? <a href={`tel:${String(t.external_phone).replace(/[^0-9+]/g, '')}`} style={{ color: '#3a6ad9', textDecoration: 'none' }}>{t.external_phone}</a>
                      : '—'}
                  </Field>
                </div>
              )}
              {/* ⚠️ TWO DIFFERENT FLAGS, and conflating them cost a round trip.
                  reason_unrecognised — the visitor never chose an option.
                  assignment_flagged  — nobody holds the assigned role, so this
                                        reached whoever it reached by fallthrough. */}
              {(t.reason_unrecognised || t.assignment_flagged) && (
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {t.reason_unrecognised && (
                    <div style={{ fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px' }}>
                      The enquiry type was not chosen on the form{t.reason_raw ? ` (received "${t.reason_raw}")` : ''} — routed to a human because the intent is unknown.
                    </div>
                  )}
                  {t.assignment_flagged && (
                    <div style={{ fontSize: 12, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px' }}>
                      No one holds <strong>{label(t.assigned_role)}</strong> — this fell through to a super-admin
                      {t.resolved_via ? ` (${t.resolved_via})` : ''}. Assign the role to route these properly.
                    </div>
                  )}
                </div>
              )}
              <div style={{ display: 'flex', gap: 24 }}>
                <Field k="Loan stage at creation"><span style={{ textTransform: 'capitalize' }}>{label(t.loan_stage_at_creation) || '—'}</span></Field>
                <Field k="Guardrail fired">{t.guardrail_fired || '—'}</Field>
              </div>
              <div style={{ display: 'flex', gap: 24 }}>
                <Field k="Source">{t.source || '—'}</Field>
                <Field k="Created">{t.created_at ? new Date(t.created_at).toLocaleString() : '—'}</Field>
                <Field k="Updated">{t.updated_at ? new Date(t.updated_at).toLocaleString() : '—'}</Field>
              </div>
            </div>

            {/* ── Conversation: replies and internal notes ─────────────────
                ⚠️ ONE LIST, TWO UNMISTAKABLY DIFFERENT ROWS. Notes and replies
                sit together because that is how a ticket carries context
                between people — but a note is amber, locked, left-barred and
                stamped "NOT SENT", and a reply is blue and stamped with the
                address it went to. Nothing about them is interchangeable at a
                glance, which is the only glance anyone gives it. */}
            <div style={{ ...card, marginBottom: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: '0 0 12px' }}>Conversation</h3>

              {msgs.length === 0 ? (
                <div style={{ color: '#9ca3af', fontSize: 13, marginBottom: 16 }}>
                  Nothing said yet. A reply goes to the requester by email; an internal note stays here.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 18 }}>
                  {msgs.map((m) => {
                    const note = m.kind === 'internal_note';
                    // ⚠️ THE REQUESTER'S OWN WORDS ARE THEIR OWN CLASS. Every
                    // inbound reply was stored in inbound_emails and rendered
                    // NOWHERE — a requester could write in, the mail could match
                    // its ticket perfectly, and no member of staff would ever
                    // see it here. That is the workspace loop broken at its
                    // centre, and it is the reason this panel could show
                    // "nothing said yet" on a ticket the requester had replied
                    // to twice.
                    const inbound = m.kind === 'inbound_reply';
                    // ⚠️ AND A WITHHELD ANSWER IS NOT A SENT ONE. Eva composed
                    // this and a guard stopped it leaving; showing it in the
                    // blue of a delivered reply would claim the requester read
                    // something nobody sent them.
                    const withheld = m.kind === 'suppressed_reply';
                    // ⚠️ AUTOMATED IS ITS OWN CLASS, NOT A VARIANT OF A REPLY.
                    // An Eva answer that looks like a colleague's reply is the
                    // attribution failure the whole triage design exists to
                    // prevent — a reader must know within a glance whether a
                    // person stood behind this. Grey and dashed, never the blue
                    // a staff reply owns, and labelled in words as well as
                    // colour because colour alone is not a claim.
                    const auto = m.kind === 'eva_answer' || m.kind === 'acknowledgement';
                    const failed = !!m.send_error;
                    const accent = inbound ? '#111827'
                      : withheld ? '#b45309'
                      : note ? '#f59e0b' : (auto ? '#9ca3af' : (failed ? '#ff082f' : '#3a6ad9'));
                    return (
                      <div key={m.id} style={{
                        // ⚠️ THE REQUESTER'S MESSAGE IS INDENTED THE OTHER WAY and
                        // sits on white. Everything else in this list is
                        // something WE said; a reader scanning it needs to see at
                        // a glance which side of the conversation each row is.
                        background: inbound ? '#fff'
                          : withheld ? '#fffbeb'
                          : note ? '#fffbeb' : (auto ? '#fbfbfc' : (failed ? '#fff0f3' : '#f9fafb')),
                        border: auto ? '1px dashed #d8dde5'
                          : withheld ? '1px dashed #fcd34d'
                          : `1px solid ${inbound ? '#d1d5db' : note ? '#fde68a' : (failed ? '#ffc5ca' : '#eef2f7')}`,
                        borderLeft: `4px ${(auto || withheld) ? 'dashed' : 'solid'} ${accent}`,
                        borderRadius: 10, padding: '10px 14px',
                        marginRight: inbound ? 0 : 24, marginLeft: inbound ? 24 : 0,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                          {/* ⚠️ THE BADGE SAYS WHERE IT WENT, not merely what it
                              is. "Internal note" is a category; "not sent to
                              anyone" is the fact that matters. */}
                          <span style={{
                            fontSize: 10, fontWeight: 800, letterSpacing: '0.04em',
                            color: inbound ? '#111827' : withheld ? '#b45309'
                              : note ? '#b45309' : (auto ? '#6b7280' : (failed ? '#ff082f' : '#3a6ad9')),
                            background: inbound ? '#e5e7eb' : withheld ? '#fef3c7'
                              : note ? '#fef3c7' : (auto ? '#f3f4f6' : (failed ? '#ffe4e6' : '#eef2fb')),
                            padding: '2px 8px', borderRadius: 9999, textTransform: 'uppercase',
                          }}>
                            {inbound ? '📥 From the requester · they wrote this'
                              : withheld ? '🚫 Written but NOT sent — nobody received this'
                              : note ? '🔒 Internal note · not sent to anyone'
                              : m.kind === 'eva_answer' ? '🤖 Automated · Eva answered the requester'
                              : m.kind === 'acknowledgement' ? '🤖 Automated · receipt only, no answer'
                              : (failed ? '⚠ Not delivered' : `✉ Replied to ${m.recipient_email || 'requester'}`)}
                          </span>
                          <span style={{ fontSize: 12, color: '#6b7280' }}>
                            {m.author_name}{m.created_at ? ` · ${new Date(m.created_at).toLocaleString()}` : ''}
                          </span>
                        </div>
                        {/* 🔑 A formatted staff reply renders from the server's HTML (utils/messageRichText, the
                            allow-list); everything else is the plain text, as always. */}
                        {hasRichBody(m)
                          ? <div style={{ fontSize: 13.5, color: '#111827', overflowWrap: 'anywhere', lineHeight: 1.6 }}><RichMessageBody msg={m} /></div>
                          : <div style={{ fontSize: 13.5, color: '#111827', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.6 }}>{m.body}</div>}
                        {/* 🔑 A reply's or note's file (the shared composer, 2026-11-02): fetched with the staff token
                            from the staff-only route and opened in a new tab; never inlined in the list. */}
                        {m.has_attachment && m.attachment_name && (
                          <button type="button" data-testid="ticket-message-attachment"
                            onClick={async () => {
                              try {
                                const r = await axios.get(`${API}/api/admin/tickets/${t.id}/messages/${m.id}/attachment`, { headers, responseType: 'blob' });
                                const url = URL.createObjectURL(r.data);
                                window.open(url, '_blank', 'noopener');
                                setTimeout(() => URL.revokeObjectURL(url), 60000);
                              } catch (_) { setMsgErr('That file could not be opened.'); }
                            }}
                            style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#3a6ad9', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>
                            📎 {m.attachment_name}
                          </button>
                        )}
                        {/* The signature AS IT WENT OUT — snapshotted server-side,
                            so a later title change doesn't rewrite history. */}
                        {withheld && (
                          <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #fcd34d', fontSize: 11.5, color: '#b45309' }}>
                            Withheld by <strong>{String(m.suppressed_reason || 'a guard').replace(/_/g, ' ')}</strong> before
                            it was sent to {m.recipient_email || 'the requester'} — this is the text they did NOT receive.
                            {' '}Send it yourself above if it should have gone.
                          </div>
                        )}
                        {!note && !auto && !inbound && !withheld && (
                          <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #eef2f7', fontSize: 11.5, color: '#6b7280' }}>
                            Signed <strong style={{ color: '#374151' }}>{m.author_name}</strong>
                            {m.author_title ? ` · ${m.author_title}` : ''}
                            {m.nmls_included && m.author_nmls ? ` · NMLS #${m.author_nmls}` : ''}
                          </div>
                        )}
                        {auto && !withheld && (
                          <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e5e7eb', fontSize: 11.5, color: '#9ca3af' }}>
                            Sent automatically to <strong style={{ color: '#6b7280' }}>{m.recipient_email || 'the requester'}</strong>.
                            No member of staff wrote or reviewed this.
                          </div>
                        )}
                        {failed && (
                          <div style={{ marginTop: 8, fontSize: 12, color: '#ff082f' }}>
                            The email did not send ({m.send_error}). Nobody received this — send it again.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ⚠️ THE COMPOSER IS REPLACED, NOT DISABLED. A greyed-out Reply
                  box still reads as "you could send this if you tried harder";
                  this says plainly who answers and why the rep is already in the
                  loop — which is the question they would otherwise raise a ticket
                  of their own to ask. */}
              {readOnly ? (
                <div style={{ marginTop: 4, padding: '12px 14px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 12.5, color: '#4b5563', lineHeight: 1.6 }}>
                  <strong style={{ color: '#374151' }}>Read only — client success answers this one.</strong>
                  <div style={{ marginTop: 4 }}>
                    You can see it because this borrower was referred by one of your partners.
                    You are copied on every reply that goes out, so there is nothing to chase.
                  </div>
                </div>
              ) : (<>
              {/* ── Composer (approved v3, 2026-11-02) ─────────────────────────
                    The mode toggle is FIRST, because it decides who reads what follows: Reply (emailed to the
                    requester) or Internal note (staff only, violet, never emailed). */}
                <div role="radiogroup" aria-label="Reply or internal note" data-testid="ticket-mode"
                  style={{ display: 'inline-flex', border: '1px solid #e5e7eb', borderRadius: 8, padding: 2, marginBottom: 10, background: '#f7f7f7' }}>
                  {[
                    { k: 'reply', lbl: 'Reply', on: '#3a6ad9' },
                    { k: 'internal_note', lbl: 'Internal note', on: '#7C58F5' },
                  ].map((o) => {
                    const active = mode === o.k;
                    return (
                      <button key={o.k} type="button" role="radio" aria-checked={active ? 'true' : 'false'} data-testid={`ticket-mode-${o.k}`}
                        onClick={() => { setMode(o.k); setMsgErr(''); setMsgOk(''); }}
                        style={{
                          border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                          background: active ? '#fff' : 'transparent', color: active ? o.on : '#6b7280',
                          boxShadow: active ? '0 1px 2px rgba(17,24,39,.12)' : 'none',
                        }}>
                        {o.lbl}
                      </button>
                    );
                  })}
                </div>

                {/* ⚠️ OFFERED ONLY TO SOMEONE IT CAN WORK FOR — a loan officer with a number on file — and OFF EVERY
                    TIME. Per-message, never remembered: "is this answer licensed activity?" is a question about this
                    reply, not about the person. */}
                {!isNote && sig?.nmls_eligible && (
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={withNmls} onChange={(e) => setWithNmls(e.target.checked)} style={{ marginTop: 2 }} />
                    <span style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
                      Add my NMLS number
                      <span style={{ color: '#9ca3af' }}> — only when this reply is licensed originator work. Leave it off for general support.</span>
                    </span>
                  </label>
                )}

                {msgErr && (
                  <div style={{ marginBottom: 10, padding: '9px 12px', background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: 8, fontSize: 13, color: '#ff082f' }}>{msgErr}</div>
                )}

                {/* ⚠️ THE DESTINATION, STATED BEFORE ANYONE TYPES — one quiet line: the literal address the mail will
                    go to and the signature it goes out over; in note mode, that only staff can see it. */}
                <StaffComposer
                  key={mode}
                  token={token}
                  onSend={sendMessage}
                  testId="ticket-composer"
                  mode={isNote ? 'note' : 'reply'}
                  topLine={(
                    <div data-testid="ticket-composer-line" style={{ fontSize: 12, marginBottom: 8, lineHeight: 1.5, color: isNote ? '#7C58F5' : '#6b7280', fontWeight: isNote ? 600 : 400 }}>
                      {isNote
                        ? 'Internal note · only staff can see this'
                        : (replyTo
                          ? <>To: <span style={{ color: '#374151' }}>{replyTo}</span>{sig ? <> · Signs as <span style={{ color: '#374151' }}>{sig.name}</span>{withNmls && sig.nmls_eligible ? ' · NMLS #…' : ''}</> : null}</>
                          : 'No email address on file for this requester — a reply cannot be sent.')}
                    </div>
                  )}
                  disabled={sending || (!isNote && !replyTo)}
                  rows={6}
                  placeholder={isNote
                    ? 'Context for whoever picks this up next — what you tried, who you spoke to, what is still open…'
                    : 'Write to the requester. They can reply straight to this email.'}
                  sendLabel={sending ? (isNote ? 'Saving…' : 'Sending…') : (isNote ? 'Save note' : 'Send reply')}
                />

                {msgOk && (
                  <div role="status" style={{ marginTop: 10, fontSize: 12, fontWeight: 700, color: '#065f46', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: '9px 12px' }}>
                    ✓ {msgOk}
                  </div>
                )}
  </>)}
            </div>

            {/* ── Ownership history ────────────────────────────────────────
                ⚠️ RENDERED ONLY WHEN A TRANSFER HAPPENED. A ticket that was
                never moved has nothing to say here, and an always-present empty
                panel trains people to skip the place the exceptions appear. */}
            {transfers.length > 0 && (
              <div style={card}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: '0 0 12px' }}>Ownership history</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {transfers.map((tr) => (
                    <div key={tr.id} style={{ fontSize: 13, color: '#374151', display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, color: '#111827' }}>
                        {label(tr.from_role) || 'Not derived'} → {label(tr.to_role)}
                      </span>
                      <span style={{ color: '#6b7280' }}>
                        by {`${tr.by_first || ''} ${tr.by_last || ''}`.trim() || tr.by_email || `#${tr.transferred_by}`}
                        {tr.transferred_at ? ` · ${new Date(tr.transferred_at).toLocaleString()}` : ''}
                      </span>
                      {tr.reason && <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>“{tr.reason}”</span>}
                    </div>
                  ))}
                </div>
                {/* ⚠️ THE ORIGINAL OWNER, STATED PLAINLY. Reading it off the
                    first transfer's from_role means it survives any number of
                    later moves. */}
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f3f4f6', fontSize: 12, color: '#6b7280' }}>
                  Originally owned by <strong style={{ color: '#374151' }}>{label(transfers[0].from_role) || 'nobody (not derived)'}</strong>.
                </div>
              </div>
            )}

            <div style={card}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: '0 0 12px' }}>Chat transcript (read-only)</h3>
              {asArray(t.transcript).length === 0 ? (
                <div style={{ color: '#9ca3af', fontSize: 13 }}>No linked transcript{t.session_id ? '' : ' (button escalation)'}.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 340, overflowY: 'auto' }}>
                  {asArray(t.transcript).map((m, i) => (
                    <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-start' : 'flex-end', maxWidth: '80%', background: m.role === 'user' ? '#eef2fb' : '#f0fde4', borderRadius: 10, padding: '8px 12px' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 2 }}>{m.role === 'user' ? 'Borrower' : 'Eva'}</div>
                      <div style={{ fontSize: 13, color: '#111827', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{m.content}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ⚠️ THE WHOLE PANEL, NOT ITS CONTROLS ONE BY ONE. Manage holds six
              write affordances — transfer, assignee, assign-to-me, status,
              resolution, save — and gating them individually leaves a heading over
              an empty column, plus one of the six behind the next time somebody
              adds a seventh. The grid collapses to one column, so removing it
              costs no layout. */}
          {readOnly ? null : (
            <>
          <div style={{ ...card, minWidth: 0 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: '0 0 14px' }}>Manage</h3>
            {err && <div style={{ marginBottom: 12, padding: '9px 12px', background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: 8, fontSize: 13, color: '#ff082f' }}>{err}</div>}

            {/* ── (2) OWNER / TEAM ─────────────────────────────────────────
                ⚠️ THROUGH THE TRANSFER ENDPOINT, NEVER A DIRECT FIELD EDIT.
                owner_role is written once at creation and is otherwise a
                point-in-time fact; POST /tickets/:id/transfer is the single
                carve-out, and it writes a ticket_owner_transfers row and the
                UPDATE in ONE transaction. A row without the update is a lie
                about what happened; an update without the row is ownership
                changing with nobody recorded as having done it.

                ⚠️ AND THIS IS NOT REASSIGNMENT. Assignee below moves who is
                WORKING it; this moves who is ACCOUNTABLE. The PUT handler
                deliberately has no code path that can write owner_* at all. */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>
                Owner <span style={{ color: '#9ca3af', fontWeight: 500 }}>· who is accountable</span>
              </div>
              {/* ⚠️ .text, NOT THE OBJECT. owner_state is { text, tone } from
                  ownerLabel(); rendering it whole is React error #31 and takes
                  the entire page down, on every ticket. The tone is carried
                  through too — "No client_success on file" is an exception and
                  should not read like an ordinary owner. */}
              <div style={{ fontSize: 13, color: ownerToneColor(t.owner_state?.tone), marginBottom: 6 }}>
                {ownerText(t.owner_state, label(t.owner_role) || 'Not derived')}
              </div>
              <select style={sel} value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
                <option value="">Move to another team…</option>
                {/* ⚠️ THE OTHER TWO TEAMS, never all seven staff roles. A ticket
                    owns to one of three teams and the hub has a tab for each;
                    moving it to a role with no tab hides it under "All". */}
                {TICKET_TEAMS.filter((r) => r.key !== t.owner_role).map((r) => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
              {moveTo && (
                <>
                  {/* ⚠️ A REASON IS ASKED FOR, NOT REQUIRED. The endpoint accepts
                      a null one; demanding text would get "n/a" typed into it,
                      which is worse than an honest blank. */}
                  <input
                    value={moveReason}
                    onChange={(e) => setMoveReason(e.target.value)}
                    placeholder="Why is this moving? (optional, but it is what the next reader sees)"
                    style={{ ...sel, marginTop: 6 }}
                  />
                  <button onClick={doTransfer} disabled={moving}
                    style={{ ...btn('#3a6ad9', '#fff'), marginTop: 8, width: '100%', opacity: moving ? 0.6 : 1 }}>
                    {moving ? 'Moving…' : `Move to ${TICKET_TEAMS.find((r) => r.key === moveTo)?.label || label(moveTo)}`}
                  </button>
                </>
              )}
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>Assignee <span style={{ color: '#9ca3af', fontWeight: 500 }}>· routed to {label(t.assigned_role)}</span></div>
              <select style={sel} value={ed.assigned_admin_id} onChange={(e) => setEd((p) => ({ ...p, assigned_admin_id: e.target.value }))}>
                <option value="">Unassigned</option>
                {roster.map((a) => <option key={a.id} value={a.id}>{a.name} ({label(a.role)})</option>)}
              </select>
              <button onClick={assignToMe} disabled={saving} style={{ ...btn('#eef2fb', '#3a6ad9'), marginTop: 8, width: '100%' }}>Assign to me</button>
            </div>

            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>Status</div>
                <select style={sel} value={ed.status} onChange={(e) => setEd((p) => ({ ...p, status: e.target.value }))}>
                  <option value="open">Open</option>
                  <option value="assigned">Assigned</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>Priority</div>
                <select style={sel} value={ed.priority} onChange={(e) => setEd((p) => ({ ...p, priority: e.target.value }))}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>

            {/* 🔑 OPTION B (approved 2026-11-02): the reminder lives where a ticket closes. */}
            <div data-testid="ticket-close-reminder" style={{ fontSize: 12, color: '#6b7280', margin: '0 0 10px', lineHeight: 1.5 }}>
              Replying doesn’t close the ticket. Close it here: set a resolution, then Status.
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>Resolution <span style={{ color: '#9ca3af', fontWeight: 500 }}>· required to close</span></div>
              <textarea rows={4} style={{ ...sel, resize: 'vertical' }} placeholder="What was done / how it was resolved…" value={ed.resolution} onChange={(e) => setEd((p) => ({ ...p, resolution: e.target.value }))} />
            </div>

            <button onClick={() => save()} disabled={saving} style={{ ...btn('#3a6ad9', '#fff'), width: '100%', opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Save changes'}</button>
            {saved && (
              <div role="status" style={{ marginTop: 8, fontSize: 12, fontWeight: 700, color: '#065f46', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                ✓ {saved}
              </div>
            )}
          </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
